import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Loader2, Copy, KeyRound, Mail, User, Send, Plus, ExternalLink, Camera } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { memberApi, formatApiError, MEMBER_TOKEN_KEY } from "@/lib/api";
import { Logo } from "@/components/site/Logo";
import { StatusPill, Thread } from "@/components/site/TicketParts";

const field = "h-11 rounded-xl border-slate-300 bg-white text-slate-900 placeholder:text-slate-400";
const EMPTY = { subject: "", priority: "normal", message: "" };
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const initials = (name = "") => name.split(" ").map((p) => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

const AvatarUploader = ({ me, onUpdated }) => {
  const [busy, setBusy] = useState(false);
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/image\/(jpe?g|png|webp|gif)/.test(file.type)) { toast.error("Please choose a JPG, PNG, WEBP or GIF image."); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image too large (max 5 MB)."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await memberApi.post("/members/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUpdated(data);
      toast.success("Profile picture updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="relative shrink-0" data-testid="member-avatar">
      <div className="h-20 w-20 overflow-hidden rounded-full border-4 border-white bg-blue-100 shadow-md ring-2 ring-blue-200">
        {me.avatar_url ? (
          <img src={`${BACKEND}${me.avatar_url}`} alt="Profile" className="h-full w-full object-cover" data-testid="member-avatar-img" />
        ) : (
          <div className="flex h-full w-full items-center justify-center font-display text-2xl font-black text-blue-600" data-testid="member-avatar-initials">{initials(me.full_name)}</div>
        )}
      </div>
      <label className="absolute -bottom-1 -right-1 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-blue-600 text-white shadow-md transition-transform hover:scale-105" title="Upload profile picture" data-testid="member-avatar-upload-label">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
        <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={onPick} disabled={busy} data-testid="member-avatar-input" />
      </label>
    </div>
  );
};

const InfoRow = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid={testId}>
    <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-600"><Icon className="h-5 w-5" /></span>
    <div className="min-w-0">
      <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="truncate font-semibold text-slate-900">{value}</p>
    </div>
  </div>
);

const NewTicket = ({ onCreated }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await memberApi.post("/members/tickets", f);
      setF(EMPTY);
      toast.success(`Ticket ${data.code} submitted`);
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-3 rounded-2xl border border-slate-200 bg-white p-5" data-testid="member-new-ticket-form">
      <h3 className="font-display flex items-center gap-2 text-lg font-bold text-slate-900"><Plus className="h-4 w-4 text-blue-600" /> New support ticket</h3>
      <div className="grid gap-3 sm:grid-cols-3">
        <Input required placeholder="Subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} className={`${field} sm:col-span-2`} data-testid="member-ticket-subject" />
        <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}>
          <SelectTrigger className={field} data-testid="member-ticket-priority"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["low", "normal", "high", "urgent"].map((p) => <SelectItem key={p} value={p} data-testid={`member-priority-${p}`} className="capitalize">{p[0].toUpperCase() + p.slice(1)} priority</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Textarea required rows={4} placeholder="Describe your issue…" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className="rounded-xl border-slate-300 bg-white text-slate-900 placeholder:text-slate-400" data-testid="member-ticket-message" />
      <button disabled={busy} data-testid="member-ticket-submit" className="font-display inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 px-6 py-3 text-sm font-bold uppercase text-white disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Submit ticket
      </button>
    </form>
  );
};

const TicketRow = ({ t, onReplied }) => {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const reply = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await memberApi.post(`/members/tickets/${t.code}/reply`, { message: msg });
      setMsg("");
      toast.success("Reply sent");
      onReplied();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`member-ticket-${t.code}`}>
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between gap-3 text-left" data-testid={`member-ticket-toggle-${t.code}`}>
        <div className="min-w-0">
          <p className="font-mono-x text-xs font-bold text-blue-600">{t.code} · {t.priority} priority</p>
          <p className="truncate font-semibold text-slate-900">{t.subject}</p>
          <p className="text-xs text-slate-500">Updated {new Date(t.updated_at).toLocaleString()}</p>
        </div>
        <StatusPill status={t.status} />
      </button>
      {open && (
        <div className="mt-4 border-t border-slate-100 pt-4">
          <Thread ticket={t} viewer="customer" />
          {t.status === "closed" ? (
            <p className="mt-4 rounded-xl bg-slate-100 p-3 text-sm font-semibold text-slate-600">This ticket is closed. Open a new one if you still need help.</p>
          ) : (
            <form onSubmit={reply} className="mt-4 space-y-3">
              <Textarea required rows={3} placeholder="Write a reply…" value={msg} onChange={(e) => setMsg(e.target.value)} className="rounded-xl border-slate-300 bg-white text-slate-900 placeholder:text-slate-400" data-testid={`member-reply-input-${t.code}`} />
              <button disabled={busy} data-testid={`member-reply-button-${t.code}`} className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-5 py-2.5 text-sm font-bold text-white disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send reply</button>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

export default function MemberDashboard() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadTickets = useCallback(async () => {
    try {
      const { data } = await memberApi.get("/members/tickets");
      setTickets(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    memberApi
      .get("/members/me")
      .then((r) => setMe(r.data))
      .catch(() => {
        localStorage.removeItem(MEMBER_TOKEN_KEY);
        nav("/login");
      })
      .finally(() => setLoading(false));
    loadTickets();
  }, [nav, loadTickets]);

  const logout = () => {
    localStorage.removeItem(MEMBER_TOKEN_KEY);
    nav("/login");
  };

  if (loading || !me)
    return <div className="flex min-h-screen items-center justify-center bg-white"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;

  return (
    <div className="min-h-screen bg-[#F3F7FF]" data-testid="member-dashboard">
      <header className="glass sticky top-0 z-40 border-b border-slate-200">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3 lg:px-8">
          <Logo testId="member-logo" small />
          <button onClick={logout} data-testid="member-logout-button" className="flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:border-red-400 hover:text-red-600"><LogOut className="h-4 w-4" /> Log out</button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10 lg:px-8">
        <div className="flex items-center gap-5" data-testid="member-header">
          <AvatarUploader me={me} onUpdated={setMe} />
          <div>
            <h1 className="font-display text-2xl font-extrabold text-slate-900 sm:text-3xl" data-testid="member-welcome">Welcome, {me.full_name}</h1>
            <p className="mt-1 text-sm text-slate-600">Your personal Start-A-TE account dashboard.</p>
          </div>
        </div>

        {/* License card */}
        <div className="mt-8 overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-blue-700 to-blue-950 p-7 text-white shadow-lg" data-testid="member-license-card">
          <div className="flex items-center gap-2 text-cyan-200"><KeyRound className="h-5 w-5" /><span className="font-mono-x text-xs font-bold uppercase tracking-widest">Software License Number</span></div>
          <button
            onClick={() => { navigator.clipboard?.writeText(me.license_number); toast.success("License copied"); }}
            data-testid="member-license-number"
            className="font-display mt-3 flex items-center gap-3 text-3xl font-black tracking-wider sm:text-4xl"
          >
            {me.license_number} <Copy className="h-5 w-5 text-cyan-200" />
          </button>
          <p className="mt-3 text-sm font-semibold text-blue-100" data-testid="member-software-label">
            Start-A-TE Software · <a href="/license-terms" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#FACC15] underline underline-offset-4" data-testid="member-license-terms-link">License terms <ExternalLink className="h-3.5 w-3.5" /></a>
          </p>
        </div>

        {/* Account info */}
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <InfoRow icon={User} label="Full name" value={me.full_name} testId="member-info-name" />
          <InfoRow icon={Mail} label="Email address" value={me.email} testId="member-info-email" />
        </div>

        {/* Tickets */}
        <div className="mt-10">
          <h2 className="font-display text-xl font-extrabold text-slate-900">Support tickets</h2>
          <p className="mt-1 text-sm text-slate-600">Open a ticket, read our replies and respond — all in one place.</p>
          <div className="mt-5"><NewTicket onCreated={loadTickets} /></div>
          <div className="mt-5 space-y-3" data-testid="member-ticket-list">
            {tickets.length === 0 ? (
              <p className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500" data-testid="member-no-tickets">You haven't opened any tickets yet.</p>
            ) : (
              tickets.map((t) => <TicketRow key={t.code} t={t} onReplied={loadTickets} />)
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
